import { supabase } from '@/shared/services/supabase';
import type { NotificationType, UserNotificationPreference } from '@/shared/types/domain.types';

export interface CreateUserNotificationPreferenceInput {
  notification_type: NotificationType;
  location_id?: string | null;
  wants_email?: boolean;
  wants_sms?: boolean;
  threshold_days_override?: number | null;
}

export interface UpdateUserNotificationPreferenceInput {
  id: string;
  wants_email?: boolean;
  wants_sms?: boolean;
  threshold_days_override?: number | null;
}

export interface UpdatePhoneNumberInput {
  phone_number: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySupabaseQuery = any;

export const notificationPreferencesService = {
  /**
   * Get current user's notification preferences
   */
  async getMyPreferences(): Promise<UserNotificationPreference[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .eq('user_id', user.id)
      .order('notification_type', { ascending: true });

    if (error) {
      console.error('Error fetching notification preferences:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference[];
  },

  /**
   * Get current user's phone number
   */
  async getMyPhoneNumber(): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .select('phone_number')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching phone number:', error);
      throw new Error(error.message);
    }

    return data?.phone_number || null;
  },

  /**
   * Get current user's SMS opt-in status
   */
  async getSmsOptInStatus(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .select('sms_opted_in_at')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching SMS opt-in status:', error);
      throw new Error(error.message);
    }

    return data?.sms_opted_in_at !== null;
  },

  /**
   * Set SMS opt-in status for current user
   */
  async setSmsOptIn(optedIn: boolean): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = optedIn 
      ? { sms_opted_in_at: new Date().toISOString() }
      : { sms_opted_in_at: null };

    const { error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .update(updateData)
      .eq('id', user.id);

    if (error) {
      console.error('Error updating SMS opt-in status:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Update current user's phone number
   */
  async updateMyPhoneNumber(phone_number: string | null): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Validate US phone format if provided
    if (phone_number && !/^\+1[0-9]{10}$/.test(phone_number)) {
      throw new Error('Phone number must be in format +1XXXXXXXXXX');
    }

    // Clear SMS opt-in consent if phone number is being removed
    const updateData: { phone_number: string | null; sms_opted_in_at?: null } = { phone_number };
    if (!phone_number) {
      updateData.sms_opted_in_at = null;
    }

    const { error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .update(updateData)
      .eq('id', user.id);

    if (error) {
      console.error('Error updating phone number:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Create a notification preference
   */
  async createPreference(input: CreateUserNotificationPreferenceInput): Promise<UserNotificationPreference> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .insert({
        user_id: user.id,
        notification_type: input.notification_type,
        location_id: input.location_id ?? null,
        wants_email: input.wants_email ?? true,
        wants_sms: input.wants_sms ?? false,
        threshold_days_override: input.threshold_days_override ?? null,
      })
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .single();

    if (error) {
      console.error('Error creating notification preference:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference;
  },

  /**
   * Update a notification preference
   */
  async updatePreference(input: UpdateUserNotificationPreferenceInput): Promise<UserNotificationPreference> {
    const updateData: Record<string, unknown> = {};
    
    if (input.wants_email !== undefined) updateData.wants_email = input.wants_email;
    if (input.wants_sms !== undefined) updateData.wants_sms = input.wants_sms;
    if (input.threshold_days_override !== undefined) updateData.threshold_days_override = input.threshold_days_override;

    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .update(updateData)
      .eq('id', input.id)
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .single();

    if (error) {
      console.error('Error updating notification preference:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference;
  },

  /**
   * Delete a notification preference
   */
  async deletePreference(id: string): Promise<void> {
    const { error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting notification preference:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Upsert preference - create or update based on notification_type and location_id
   */
  async upsertPreference(input: CreateUserNotificationPreferenceInput): Promise<UserNotificationPreference> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .upsert(
        {
          user_id: user.id,
          notification_type: input.notification_type,
          location_id: input.location_id ?? null,
          wants_email: input.wants_email ?? true,
          wants_sms: input.wants_sms ?? false,
          threshold_days_override: input.threshold_days_override ?? null,
        },
        {
          onConflict: 'user_id,notification_type,location_id',
        }
      )
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .single();

    if (error) {
      console.error('Error upserting notification preference:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference;
  },

  /**
   * Send test notification to current user
   */
  async sendTestNotification(channel: 'email' | 'sms'): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.functions.invoke('send-notifications', {
      body: {
        channel,
        test: true,
        test_user_id: user.id,
      },
    });

    if (error) {
      console.error('Error sending test notification:', error);
      throw new Error(error.message || 'Failed to send test notification');
    }
  },

  // ============================================================================
  // ADMIN FUNCTIONS
  // ============================================================================

  /**
   * Get all user notification preferences (admin only)
   */
  async getAllUserPreferences(): Promise<(UserNotificationPreference & { user: { id: string; email: string; name: string; phone_number: string | null } })[]> {
    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        ),
        user:users (
          id,
          email,
          name,
          phone_number
        )
      `)
      .order('user_id', { ascending: true });

    if (error) {
      console.error('Error fetching all user preferences:', error);
      throw new Error(error.message);
    }

    return data as (UserNotificationPreference & { user: { id: string; email: string; name: string; phone_number: string | null } })[];
  },

  /**
   * Get preferences for a specific user (admin only)
   */
  async getUserPreferences(userId: string): Promise<UserNotificationPreference[]> {
    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .eq('user_id', userId)
      .order('notification_type', { ascending: true });

    if (error) {
      console.error('Error fetching user preferences:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference[];
  },

  /**
   * Update a user's phone number (admin only)
   */
  async updateUserPhoneNumber(userId: string, phone_number: string | null): Promise<void> {
    // Validate US phone format if provided
    if (phone_number && !/^\+1[0-9]{10}$/.test(phone_number)) {
      throw new Error('Phone number must be in format +1XXXXXXXXXX');
    }

    const { error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .update({ phone_number })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user phone number:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Create/update preference for a specific user (admin only)
   */
  async upsertUserPreference(
    userId: string,
    input: CreateUserNotificationPreferenceInput
  ): Promise<UserNotificationPreference> {
    const { data, error } = await (supabase
      .from('user_notification_preferences') as AnySupabaseQuery)
      .upsert(
        {
          user_id: userId,
          notification_type: input.notification_type,
          location_id: input.location_id ?? null,
          wants_email: input.wants_email ?? true,
          wants_sms: input.wants_sms ?? false,
          threshold_days_override: input.threshold_days_override ?? null,
        },
        {
          onConflict: 'user_id,notification_type,location_id',
        }
      )
      .select(`
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `)
      .single();

    if (error) {
      console.error('Error upserting user notification preference:', error);
      throw new Error(error.message);
    }

    return data as UserNotificationPreference;
  },

  /**
   * Get SMS opt-in status for a specific user (admin only)
   */
  async getUserSmsOptInStatus(userId: string): Promise<boolean> {
    const { data, error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .select('sms_opted_in_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user SMS opt-in status:', error);
      throw new Error(error.message);
    }

    return data?.sms_opted_in_at !== null;
  },

  /**
   * Set SMS opt-in status for a specific user (admin only)
   */
  async setUserSmsOptIn(userId: string, optedIn: boolean): Promise<void> {
    const updateData = optedIn 
      ? { sms_opted_in_at: new Date().toISOString() }
      : { sms_opted_in_at: null };

    const { error } = await (supabase
      .from('users') as AnySupabaseQuery)
      .update(updateData)
      .eq('id', userId);

    if (error) {
      console.error('Error updating user SMS opt-in status:', error);
      throw new Error(error.message);
    }
  },
};
